import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

/**
 * Analyse Results V2 - Flow-First Design
 * - Carte HÉROS avec verdict clair
 * - KPIs secondaires
 * - Indicateur de risque
 * - Projections (paywall naturel)
 */
const AnalysePageV2 = () => {
  const navigate = useNavigate();
  const [showPaywall] = useState(true);

  const analysis = {
    address: '123 Rue Example, Montréal',
    verdict: 'Bon investissement',
    verdictType: 'positive',
    verdictExplanation: 'Cashflow positif dès le jour 1 avec un potentiel de croissance intéressant.',
    
    mainKPI: {
      label: 'Cashflow mensuel',
      value: 847,
      unit: '$/mois',
      trend: '+12%',
      trendDirection: 'up'
    },
    
    secondaryKPIs: [
      { label: 'ROI Cash-on-Cash', value: '8.2%', icon: 'TrendingUp', color: '#22c55e' },
      { label: 'Cap Rate', value: '5.8%', icon: 'Target', color: '#3b82f6' }
    ],
    
    riskScore: 3,
    riskFactors: [
      { label: 'Ratio de couverture', status: 'good', value: '1.25' },
      { label: 'Vacance estimée', status: 'warning', value: '5%' },
      { label: 'Liquidité', status: 'good', value: 'Bonne' }
    ]
  };

  const getVerdictStyle = (type) => {
    switch (type) {
      case 'positive':
        return {
          bg: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(16, 185, 129, 0.1))',
          border: 'rgba(34, 197, 94, 0.3)',
          icon: 'ThumbsUp',
          iconColor: '#22c55e'
        };
      case 'negative':
        return {
          bg: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.1))',
          border: 'rgba(239, 68, 68, 0.3)',
          icon: 'ThumbsDown',
          iconColor: '#ef4444'
        };
      default:
        return {
          bg: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.1))',
          border: 'rgba(251, 191, 36, 0.3)',
          icon: 'Minus',
          iconColor: '#fbbf24'
        };
    }
  };

  const verdictStyle = getVerdictStyle(analysis.verdictType);

  const getRiskLabel = (score) => {
    if (score >= 4) return { label: 'Risque faible', color: '#22c55e' };
    if (score >= 3) return { label: 'Risque modéré', color: '#fbbf24' };
    return { label: 'Risque élevé', color: '#ef4444' };
  };

  const riskInfo = getRiskLabel(analysis.riskScore);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: 'white'
    }}>
      <header style={{
        padding: '1.5rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <button
          onClick={() => navigate('/v2')}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem'
          }}
        >
          <Icon name="ArrowLeft" size={18} />
          <span>Retour</span>
        </button>

        <button style={{
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: '8px',
          padding: '0.5rem 1rem',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.875rem'
        }}>
          <Icon name="Share2" size={16} />
          <span>Partager</span>
        </button>
      </header>

      <main style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>
            Analyse de propriété
          </p>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>{analysis.address}</h1>
        </div>

        {/* Hero Card - Verdict */}
        <div style={{
          background: verdictStyle.bg,
          border: `1px solid ${verdictStyle.border}`,
          borderRadius: '20px',
          padding: '2rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '14px',
              background: 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Icon name={verdictStyle.icon} size={28} color={verdictStyle.iconColor} />
            </div>
            
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                marginBottom: '0.5rem',
                color: verdictStyle.iconColor
              }}>
                {analysis.verdict}
              </h2>
              <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
                {analysis.verdictExplanation}
              </p>
            </div>
          </div>

          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'baseline',
            gap: '1rem'
          }}>
            <div>
              <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.25rem' }}>
                {analysis.mainKPI.label}
              </p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                <span style={{ fontSize: '2.5rem', fontWeight: 700, color: '#22c55e' }}>
                  +{analysis.mainKPI.value}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>{analysis.mainKPI.unit}</span>
              </div>
            </div>
            {analysis.mainKPI.trend && (
              <div style={{
                background: 'rgba(34, 197, 94, 0.2)',
                padding: '0.25rem 0.75rem',
                borderRadius: '100px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                color: '#22c55e',
                fontSize: '0.875rem'
              }}>
                <Icon name="TrendingUp" size={14} />
                <span>{analysis.mainKPI.trend}</span>
              </div>
            )}
          </div>
        </div>

        {/* Secondary KPIs */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          {analysis.secondaryKPIs.map((kpi, index) => (
            <div key={index} style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '16px',
              padding: '1.25rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Icon name={kpi.icon} size={18} color={kpi.color} />
                <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)' }}>{kpi.label}</span>
              </div>
              <p style={{ fontSize: '1.5rem', fontWeight: 700, color: kpi.color }}>{kpi.value}</p>
            </div>
          ))}
        </div>

        {/* Risk Indicator */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Évaluation du risque</h3>
            <span style={{
              background: `${riskInfo.color}20`,
              color: riskInfo.color,
              padding: '0.25rem 0.75rem',
              borderRadius: '100px',
              fontSize: '0.875rem',
              fontWeight: 500
            }}>
              {riskInfo.label}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '4px', marginBottom: '1rem' }}>
            {[1, 2, 3, 4, 5].map((level) => (
              <div key={level} style={{
                flex: 1,
                height: '8px',
                borderRadius: '4px',
                background: level <= analysis.riskScore ? riskInfo.color : 'rgba(255,255,255,0.1)'
              }} />
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {analysis.riskFactors.map((factor, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.875rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: factor.status === 'good' ? '#22c55e' : 
                               factor.status === 'warning' ? '#fbbf24' : '#ef4444'
                  }} />
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{factor.label}</span>
                </div>
                <span style={{ fontWeight: 500 }}>{factor.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Projections - Paywall */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: '1.5rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: 600, 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Icon name="TrendingUp" size={18} />
            <span>Projections sur 10 ans</span>
          </h3>

          <div style={{
            height: '200px',
            background: 'linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'flex-end',
            padding: '1rem',
            gap: '8px',
            filter: showPaywall ? 'blur(6px)' : 'none',
            opacity: showPaywall ? 0.5 : 1
          }}>
            {[40, 55, 45, 60, 75, 70, 85, 90, 95, 100].map((height, i) => (
              <div key={i} style={{
                flex: 1,
                height: `${height}%`,
                background: 'linear-gradient(to top, #3b82f6, #8b5cf6)',
                borderRadius: '4px 4px 0 0'
              }} />
            ))}
          </div>

          {showPaywall && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              background: 'rgba(15, 23, 42, 0.95)',
              padding: '2rem',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.1)',
              maxWidth: '320px',
              width: '90%'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2))',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Icon name="Film" size={28} color="#60a5fa" />
              </div>
              <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Tu vois la photo.
              </h4>
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                Débloque le film avec l'essai gratuit 14 jours.
              </p>
              <button
                onClick={() => navigate('/subscription-pricing')}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  color: 'white',
                  border: 'none',
                  padding: '0.875rem 1.5rem',
                  borderRadius: '10px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Activer l'essai gratuit
              </button>
            </div>
          )}
        </div>

        {/* Next Action CTA */}
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Prochaine étape
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '1rem', fontSize: '0.875rem' }}>
            Intéressé par cet immeuble? Passe à l'étape d'achat.
          </p>
          <button
            onClick={() => navigate('/v2/achat')}
            style={{
              background: 'white',
              color: '#0f172a',
              border: 'none',
              padding: '0.875rem 1.5rem',
              borderRadius: '10px',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <span>Commencer le processus d'achat</span>
            <Icon name="ArrowRight" size={16} />
          </button>
        </div>
      </main>
    </div>
  );
};

export default AnalysePageV2;
